import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useValueChanged } from '@/lib/useValueChanged';

/* ══════════════════════════════════════════════════════
 *  資料視覺化小元件。純呈現、無狀態，各頁共用。
 * ══════════════════════════════════════════════════════ */

/** 信心值：數字＋長條。裸數字看不出高低，掃讀時要靠長度判斷。 */
export function ConfidenceBar({ value }: { value?: number | null }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  const pct = Math.max(0, Math.min(1, value)) * 100;
  // 門檻沿用平台慣例：0.8 以上算高、0.6 以下偏低
  const tone = value >= 0.8 ? 'bg-success' : value >= 0.6 ? 'bg-warn' : 'bg-muted-foreground';
  return (
    <div className="flex items-center justify-end gap-2">
      <span className="tabular text-[12px]">{value.toFixed(2)}</span>
      <span className="h-1.5 w-10 overflow-hidden rounded-full bg-secondary" aria-hidden>
        <span
          className={cn('block h-full rounded-full', tone)}
          style={{ width: `${pct}%`, transition: 'width var(--motion-base) var(--ease-out)' }}
        />
      </span>
    </div>
  );
}

/** 進度條，用於部署／上傳進度。
 *  live：進行中（0% 到 100% 之間）時加斜紋循環，循環本身就是「還在動」的訊息。 */
export function ProgressBar({
  value, total, tone = 'primary', live = false,
}: {
  value: number;
  total: number;
  tone?: 'primary' | 'success' | 'warn' | 'danger';
  live?: boolean;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const bg = {
    primary: 'bg-primary', success: 'bg-success',
    warn: 'bg-warn', danger: 'bg-destructive',
  }[tone];
  return (
    <div className="flex items-center gap-2.5">
      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary" aria-hidden>
        <span
          className={cn('block h-full rounded-full', bg, live && pct > 0 && pct < 100 && 'progress-live')}
          style={{
            width: `${pct}%`,
            transition: 'width var(--motion-slow) var(--ease-out)',
          }}
        />
      </span>
      <span className="tabular w-[52px] shrink-0 text-right text-[11.5px] text-muted-foreground">
        {value}/{total}
      </span>
    </div>
  );
}

/** 統計卡。數字是主角，用 display 字體放大並收緊字距。 */
export function StatCard({
  icon, label, value, sub, tone = 'default', highlight,
}: {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'success' | 'warn' | 'danger' | 'info';
  highlight?: boolean;
}) {
  // 週期重抓時數字會換，閃一下讓人知道它更新了
  const ticked = useValueChanged(typeof value === 'number' ? value : String(value));

  const iconTone = {
    default: 'bg-secondary text-muted-foreground',
    success: 'bg-success-soft text-success-on',
    warn: 'bg-warn-soft text-warn-on',
    danger: 'bg-accent text-accent-foreground',
    info: 'bg-info-soft text-info-on',
  }[tone];

  return (
    <div
      className={cn(
        'card-interactive rounded-lg border bg-card p-5',
        highlight ? 'border-primary/40 shadow-[0_0_0_3px_hsl(var(--primary)/0.06)]' : 'border-border',
      )}
    >
      <div className="flex items-center gap-2.5">
        {icon && (
          <span className={cn('grid h-7 w-7 shrink-0 place-items-center rounded-sm', iconTone)}>
            {icon}
          </span>
        )}
        <span className="text-[11.5px] font-medium text-muted-foreground">{label}</span>
      </div>
      <p className={cn(
        'mt-3 inline-block font-display text-[30px] font-bold leading-none tracking-[-0.03em]',
        ticked && 'value-tick',
      )}>
        {value}
      </p>
      {sub && <p className="mt-2 text-[11.5px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

/** 空狀態：有圖示與說明，比一行灰字有質感。 */
export function EmptyState({
  icon, title, hint,
}: {
  icon?: ReactNode;
  title: string;
  hint?: ReactNode;
}) {
  return (
    <div className="grid place-items-center px-6 py-16 text-center">
      {icon && (
        <span className="pop mb-3 grid h-12 w-12 place-items-center rounded-lg
                         bg-secondary text-muted-foreground">
          {icon}
        </span>
      )}
      <p className="text-[13px] font-semibold">{title}</p>
      {hint && <p className="mt-1.5 max-w-sm text-[12px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
